import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';

import {
  TCoursePermissionTemplateGroupCriteraOption,
  TCoursePermissionTemplateRoleCriteraOption,
  TCoursePermissionTemplateUserCriteraOption,
  coursePermissionTemplateGroupCriteriaOptions,
  coursePermissionTemplateRoleCriteriaOptions,
  coursePermissionTemplateUserCriteriaOptions,
} from '../../../../../../constants';
import {
  adminCreateCoursePermissionTemplate,
  adminUpdateCoursePermissionTemplate,
  COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS,
  COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS,
  COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS,
  ICoursePermissionTemplate,
  TAdminCreateCoursePermissionTemplate,
  TAdminUpdateCoursePermissionTemplate,
} from '../../../../../../store/V2/CoursePermissionTemplatesSlice';
import { IUser } from '../../../../../../store/UserSlice';
import { IGroup } from '../../../../../../store/V2/GroupsSlice';
import { IRole } from '../../../../../../store/V2/RolesSlice';
import { capitalizeFirstLetter } from '../../../../../../helper';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import GroupListDropdown, {
  TGroupOption,
} from '../../../../../../components/Inputs/admin/GroupListDropdown';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/admin/UserListDropdown';
import UserRoleListDropDown, {
  TUserRoleSelectOption,
} from '../../../../../../components/Inputs/admin/UserRoleListDropDown';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';

interface AddCoursePermissionTemplateProps {
  open: boolean;
  onClose: () => void;
  coursePermissionTemplate?: ICoursePermissionTemplate;
  readOnly?: boolean;
  duplicate?: boolean;
}

type TState = {
  name: string;
  roleChecked: boolean;
  selectedRoleCriteria: TCoursePermissionTemplateRoleCriteraOption;
  selectedUserRole: TUserRoleSelectOption | null;
  selectedUserRoles: TUserRoleSelectOption[];
  groupChecked: boolean;
  selectedGroupCriteria: TCoursePermissionTemplateGroupCriteraOption;
  selectedGroup: TGroupOption | null;
  selectedGroups: TGroupOption[];
  userChecked: boolean;
  selectedUserCriteria: TCoursePermissionTemplateUserCriteraOption;
  selectedUser: TUserSelectOption | null;
  selectedUsers: TUserSelectOption[];
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  name: '',
  roleChecked: false,
  selectedRoleCriteria: coursePermissionTemplateRoleCriteriaOptions[0],
  selectedUserRole: null,
  selectedUserRoles: [],
  groupChecked: false,
  selectedGroupCriteria: coursePermissionTemplateGroupCriteriaOptions[0],
  selectedGroup: null,
  selectedGroups: [],
  userChecked: false,
  selectedUserCriteria: coursePermissionTemplateUserCriteriaOptions[0],
  selectedUser: null,
  selectedUsers: [],
};

const AddCoursePermissionTemplate: FunctionComponent<AddCoursePermissionTemplateProps> = ({
  open,
  onClose,
  coursePermissionTemplate,
  readOnly = false,
  duplicate = false,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  let mode: 'new' | 'edit' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Course Permission Template';

  if (coursePermissionTemplate) {
    mode = 'edit';
    modalTitle = 'Edit Course Permission Template';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Course Permission Template Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Course Permission Template';
    }
  }

  const isSubmitDisabled =
    !state.name.trim().length ||
    (state.groupChecked && !state.selectedGroupCriteria) ||
    (state.groupChecked &&
      state.selectedGroupCriteria.value !==
        COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS.ALL &&
      !state.selectedGroups.length) ||
    (state.roleChecked && !state.selectedRoleCriteria) ||
    (state.roleChecked &&
      state.selectedRoleCriteria.value !==
        COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS.ALL &&
      !state.selectedUserRoles.length) ||
    (state.userChecked && !state.selectedUserCriteria) ||
    (state.roleChecked &&
      state.selectedUserCriteria.value !==
        COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS.ALL &&
      !state.selectedUsers.length);

  useEffect(() => {
    if (mode !== 'new' && coursePermissionTemplate) {
      setState((prevState) => {
        const newStateData = { ...prevState };
        const newSelectedGroups: TGroupOption[] = [];
        const newSelectedUserRoles: TUserRoleSelectOption[] = [];
        const newSelectedUsers: TUserSelectOption[] = [];

        if (coursePermissionTemplate.name) {
          newStateData.name = coursePermissionTemplate.name;
        }

        if (coursePermissionTemplate.group) {
          newStateData.groupChecked = coursePermissionTemplate.group;

          if (coursePermissionTemplate.groupCriteria) {
            const _groups = (coursePermissionTemplate.groups || []) as IGroup[];
            const matchingGroupCriteria = coursePermissionTemplateGroupCriteriaOptions.find(
              (item) => item.value === coursePermissionTemplate.groupCriteria
            );

            if (matchingGroupCriteria) {
              newStateData.selectedGroupCriteria = matchingGroupCriteria;
            }

            _groups.forEach((item) => {
              newSelectedGroups.push({
                label: item.name,
                value: item._id,
              });
            });
          }
        }

        if (coursePermissionTemplate.role) {
          newStateData.roleChecked = coursePermissionTemplate.role;

          if (coursePermissionTemplate.roleCriteria) {
            const _roles = (coursePermissionTemplate.roles || []) as IRole[];

            const matchingRoleCriteria = coursePermissionTemplateRoleCriteriaOptions.find(
              (item) => item.value === coursePermissionTemplate.roleCriteria
            );

            if (matchingRoleCriteria) {
              newStateData.selectedRoleCriteria = matchingRoleCriteria;
            }

            _roles.forEach((item) => {
              newSelectedUserRoles.push({
                label: item.name,
                value: item._id,
              });
            });
          }
        }

        if (coursePermissionTemplate.user) {
          newStateData.userChecked = coursePermissionTemplate.user;

          if (coursePermissionTemplate.userCriteria) {
            const _users = (coursePermissionTemplate.users || []) as IUser[];
            const matchingUserCriteria = coursePermissionTemplateUserCriteriaOptions.find(
              (item) => item.value === coursePermissionTemplate.userCriteria
            );

            if (matchingUserCriteria) {
              newStateData.selectedUserCriteria = matchingUserCriteria;
            }

            _users.forEach((item) => {
              let _name = item.firstname;

              if (item.lastname) {
                _name += ` ${item.lastname}`;
              }

              newSelectedUsers.push({
                label: _name,
                value: item._id,
                roleName: item.role.name,
              });
            });
          }
        }

        newStateData.selectedGroups = newSelectedGroups;
        newStateData.selectedUserRoles = newSelectedUserRoles;
        newStateData.selectedUsers = newSelectedUsers;

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleAddUserRole = () => {
    setState((prevState) => {
      const newStateData = { ...prevState, selectedUserRole: null };

      if (
        state.selectedUserRole &&
        !newStateData.selectedUserRoles.some(
          (item) =>
            state.selectedUserRole &&
            item.value === state.selectedUserRole.value
        )
      ) {
        newStateData.selectedUserRoles = [
          ...prevState.selectedUserRoles,
          state.selectedUserRole,
        ];
      }

      return newStateData;
    });
  };

  const removeSelectedUserRole = (role: TUserRoleSelectOption) => {
    setState((prevState) => {
      const newStateData = { ...prevState };

      newStateData.selectedUserRoles = [...prevState.selectedUserRoles].filter(
        (item) => item.value !== role.value
      );

      return newStateData;
    });
  };

  const handleAddGroup = () => {
    setState((prevState) => {
      const newStateData = { ...prevState, selectedGroup: null };

      if (
        state.selectedGroup &&
        !newStateData.selectedGroups.some(
          (item) =>
            state.selectedGroup && item.value === state.selectedGroup.value
        )
      ) {
        newStateData.selectedGroups = [
          ...prevState.selectedGroups,
          state.selectedGroup,
        ];
      }

      return newStateData;
    });
  };

  const removeSelectedGroup = (group: TGroupOption) => {
    setState((prevState) => {
      const newStateData = { ...prevState };

      newStateData.selectedGroups = [...prevState.selectedGroups].filter(
        (item) => item.value !== group.value
      );

      return newStateData;
    });
  };

  const handleAddUser = () => {
    setState((prevState) => {
      const newStateData = { ...prevState, selectedUser: null };

      if (
        state.selectedUser &&
        !newStateData.selectedUsers.some(
          (item) =>
            state.selectedUser && item.value === state.selectedUser.value
        )
      ) {
        newStateData.selectedUsers = [
          ...prevState.selectedUsers,
          state.selectedUser,
        ];
      }

      return newStateData;
    });
  };

  const removeSelectedUser = (user: TUserSelectOption) => {
    setState((prevState) => {
      const newStateData = { ...prevState };

      newStateData.selectedUsers = [...prevState.selectedUsers].filter(
        (item) => item.value !== user.value
      );

      return newStateData;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateCoursePermissionTemplate = {
        name: state.name,
        group: state.groupChecked,
        role: state.roleChecked,
        user: state.userChecked,
      };

      if (state.groupChecked) {
        apiData.groupCriteria = state.selectedGroupCriteria.value;

        if (
          state.selectedGroupCriteria.value !==
            COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS.ALL &&
          state.selectedGroups.length
        ) {
          apiData.groups = state.selectedGroups.map((item) => item.value);
        }
      }

      if (state.roleChecked) {
        apiData.roleCriteria = state.selectedRoleCriteria.value;

        if (
          state.selectedRoleCriteria.value !==
            COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS.ALL &&
          state.selectedUserRoles.length
        ) {
          apiData.roles = state.selectedUserRoles.map((item) => item.value);
        }
      }

      if (state.userChecked) {
        apiData.userCriteria = state.selectedUserCriteria.value;

        if (
          state.selectedUserCriteria.value !==
            COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS.ALL &&
          state.selectedUsers.length
        ) {
          apiData.users = state.selectedUsers.map((item) => item.value);
        }
      }

      const res: any = await dispatch(
        adminCreateCoursePermissionTemplate(apiData)
      );

      if (res.payload) {
        onClose();
      }
    } else if (mode === 'edit' && coursePermissionTemplate) {
      const apiData: TAdminUpdateCoursePermissionTemplate = {
        id: coursePermissionTemplate._id,
        name: state.name,
        group: state.groupChecked,
        role: state.roleChecked,
        user: state.userChecked,
      };

      if (state.groupChecked) {
        apiData.groupCriteria = state.selectedGroupCriteria.value;

        if (
          state.selectedGroupCriteria.value !==
            COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS.ALL &&
          state.selectedGroups.length
        ) {
          apiData.groups = state.selectedGroups.map((item) => item.value);
        }
      }

      if (state.roleChecked) {
        apiData.roleCriteria = state.selectedRoleCriteria.value;

        if (
          state.selectedRoleCriteria.value !==
            COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS.ALL &&
          state.selectedUserRoles.length
        ) {
          apiData.roles = state.selectedUserRoles.map((item) => item.value);
        }
      }

      if (state.userChecked) {
        apiData.userCriteria = state.selectedUserCriteria.value;

        if (
          state.selectedUserCriteria.value !==
            COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS.ALL &&
          state.selectedUsers.length
        ) {
          apiData.users = state.selectedUsers.map((item) => item.value);
        }
      }

      const res: any = await dispatch(
        adminUpdateCoursePermissionTemplate(apiData)
      );

      if (res.payload) {
        onClose();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Name *"
                  placeholder="Enter here..."
                  value={state.name}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <Stack>
                  <FormControlLabel
                    label="Groups"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.groupChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            groupChecked: value,
                          })
                        }
                      />
                    }
                  />
                  {state.groupChecked ? (
                    <Collapse in>
                      <Stack gap="15px" p="15px 0">
                        <Autocomplete
                          fullWidth
                          disableClearable
                          size="small"
                          readOnly={readOnly}
                          value={state.selectedGroupCriteria}
                          options={coursePermissionTemplateGroupCriteriaOptions}
                          popupIcon={TPAutoCompleteArrowIconComponent}
                          onChange={(event, value) =>
                            updateStateAttributes({
                              selectedGroupCriteria: value,
                            })
                          }
                          getOptionLabel={(option) =>
                            capitalizeFirstLetter(option.label)
                          }
                          renderInput={(params) => (
                            <TPTextField
                              {...params}
                              {...textFieldProps}
                              size="small"
                              label="Selection criteria *"
                              placeholder="Select"
                            />
                          )}
                        />
                        {state.selectedGroupCriteria.value !==
                        COURSE_PERMISSION_TEMPLATE_GROUP_CRITERIAS.ALL ? (
                          <>
                            <div>
                              <Typography fontSize="14px" fontWeight={600}>
                                Groups *
                              </Typography>
                            </div>
                            {mode !== 'view' ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap="10px"
                              >
                                <GroupListDropdown
                                  selectedItem={state.selectedGroup}
                                  handleChange={(e, value) =>
                                    updateStateAttributes({
                                      selectedGroup: value,
                                    })
                                  }
                                  componentProps={{
                                    fullWidth: true,
                                  }}
                                  textFieldProps={{
                                    label: 'Group',
                                    placeholder: 'Select',
                                    ...textFieldProps,
                                  }}
                                />
                                <DarkButton
                                  variant="contained"
                                  color="inherit"
                                  sx={{ ...defaultButtonStyles }}
                                  onClick={handleAddGroup}
                                  disabled={!state.selectedGroup}
                                >
                                  Add
                                </DarkButton>
                              </Stack>
                            ) : null}
                            {state.selectedGroups.map((item) => {
                              return (
                                <Stack
                                  key={item.value}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography fontSize="14px" fontWeight={500}>
                                    {item.label}
                                  </Typography>
                                  {mode !== 'view' ? (
                                    <IconButton
                                      onClick={() => removeSelectedGroup(item)}
                                    >
                                      <DeleteIcon sx={{ color: '#1E293B' }} />
                                    </IconButton>
                                  ) : null}
                                </Stack>
                              );
                            })}
                          </>
                        ) : null}
                      </Stack>
                    </Collapse>
                  ) : null}
                  <FormControlLabel
                    label="Roles"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.roleChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            roleChecked: value,
                          })
                        }
                      />
                    }
                  />
                  {state.roleChecked ? (
                    <Collapse in>
                      <Stack gap="15px" p="15px 0">
                        <Autocomplete
                          fullWidth
                          disableClearable
                          size="small"
                          readOnly={readOnly}
                          value={state.selectedRoleCriteria}
                          options={coursePermissionTemplateRoleCriteriaOptions}
                          popupIcon={TPAutoCompleteArrowIconComponent}
                          onChange={(event, value) =>
                            updateStateAttributes({
                              selectedRoleCriteria: value,
                            })
                          }
                          getOptionLabel={(option) =>
                            capitalizeFirstLetter(option.label)
                          }
                          renderInput={(params) => (
                            <TPTextField
                              {...params}
                              {...textFieldProps}
                              size="small"
                              label="Selection criteria *"
                              placeholder="Select"
                            />
                          )}
                        />
                        {state.selectedRoleCriteria.value !==
                        COURSE_PERMISSION_TEMPLATE_ROLE_CRITERIAS.ALL ? (
                          <>
                            <div>
                              <Typography fontSize="14px" fontWeight={600}>
                                Roles *
                              </Typography>
                            </div>
                            {mode !== 'view' ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap="10px"
                              >
                                <UserRoleListDropDown
                                  selectedItem={state.selectedUserRole}
                                  handleChange={(e, value) =>
                                    updateStateAttributes({
                                      selectedUserRole: value,
                                    })
                                  }
                                  componentProps={{
                                    fullWidth: true,
                                  }}
                                  textFieldProps={{
                                    label: 'Role',
                                    placeholder: 'Select',
                                    ...textFieldProps,
                                  }}
                                />
                                <DarkButton
                                  variant="contained"
                                  color="inherit"
                                  sx={{ ...defaultButtonStyles }}
                                  onClick={handleAddUserRole}
                                  disabled={!state.selectedUserRole}
                                >
                                  Add
                                </DarkButton>
                              </Stack>
                            ) : null}
                            {state.selectedUserRoles.map((item) => {
                              return (
                                <Stack
                                  key={item.value}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography fontSize="14px" fontWeight={500}>
                                    {item.label}
                                  </Typography>
                                  {mode !== 'view' ? (
                                    <IconButton
                                      onClick={() =>
                                        removeSelectedUserRole(item)
                                      }
                                    >
                                      <DeleteIcon sx={{ color: '#1E293B' }} />
                                    </IconButton>
                                  ) : null}
                                </Stack>
                              );
                            })}
                          </>
                        ) : null}
                      </Stack>
                    </Collapse>
                  ) : null}
                  <FormControlLabel
                    label="Users"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.userChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            userChecked: value,
                          })
                        }
                      />
                    }
                  />
                  {state.userChecked ? (
                    <Collapse in>
                      <Stack gap="15px" p="15px 0">
                        <Autocomplete
                          fullWidth
                          disableClearable
                          size="small"
                          readOnly={readOnly}
                          value={state.selectedUserCriteria}
                          options={coursePermissionTemplateUserCriteriaOptions}
                          popupIcon={TPAutoCompleteArrowIconComponent}
                          onChange={(event, value) =>
                            updateStateAttributes({
                              selectedUserCriteria: value,
                            })
                          }
                          getOptionLabel={(option) =>
                            capitalizeFirstLetter(option.label)
                          }
                          renderInput={(params) => (
                            <TPTextField
                              {...params}
                              {...textFieldProps}
                              size="small"
                              label="Selection criteria *"
                              placeholder="Select"
                            />
                          )}
                        />
                        {state.selectedUserCriteria.value !==
                        COURSE_PERMISSION_TEMPLATE_USER_CRITERIAS.ALL ? (
                          <>
                            <div>
                              <Typography fontSize="14px" fontWeight={600}>
                                Users *
                              </Typography>
                            </div>
                            {mode !== 'view' ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap="10px"
                              >
                                <UserListDropdown
                                  selectedItem={state.selectedUser}
                                  handleChange={(e, value) =>
                                    updateStateAttributes({
                                      selectedUser: value,
                                    })
                                  }
                                  componentProps={{
                                    fullWidth: true,
                                  }}
                                  textFieldProps={{
                                    label: 'User',
                                    placeholder: 'Select',
                                    ...textFieldProps,
                                  }}
                                />
                                <DarkButton
                                  variant="contained"
                                  color="inherit"
                                  sx={{ ...defaultButtonStyles }}
                                  onClick={handleAddUser}
                                  disabled={!state.selectedUser}
                                >
                                  Add
                                </DarkButton>
                              </Stack>
                            ) : null}
                            {state.selectedUsers.map((item) => {
                              return (
                                <Stack
                                  key={item.value}
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Typography fontSize="14px" fontWeight={500}>
                                    {item.label}
                                  </Typography>
                                  {mode !== 'view' ? (
                                    <IconButton
                                      onClick={() => removeSelectedUser(item)}
                                    >
                                      <DeleteIcon sx={{ color: '#1E293B' }} />
                                    </IconButton>
                                  ) : null}
                                </Stack>
                              );
                            })}
                          </>
                        ) : null}
                      </Stack>
                    </Collapse>
                  ) : null}
                </Stack>
              </FormGroup>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AddCoursePermissionTemplate;
